import React, { useState } from 'react';
import { Gift, Users, Send, DollarSign, ArrowRight, MessageSquare } from 'lucide-react';
import { ReferralModal } from './ReferralModal';
import { Reveal, Words, CountUp } from './motion/Reveal';

const STEPS = [[Users, 'Trouvez un ami'], [Send, 'Envoyez-le chez nous'], [DollarSign, 'Recevez 350 $ comptant']];
const SMS_BODY = encodeURIComponent('Salut! Credit Auto Reset m’a aidé pour mon financement auto. Fais ta demande ici et mentionne mon nom : https://creditautoreset.ca/demander-un-pret-auto');

const Confetti = () => (
  <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
    {['top-6 left-8 bg-[#4ade80]', 'top-16 right-12 bg-yellow-300', 'bottom-10 left-16 bg-emerald-300', 'top-1/2 right-6 bg-white/70', 'bottom-6 right-1/3 bg-[#4ade80]', 'top-8 left-1/2 bg-yellow-200'].map((c, i) => (
      <span key={c} className={`absolute w-2.5 h-2.5 rounded-sm rotate-45 floaty ${c}`} style={{ animationDelay: `${i * 0.5}s` }} />
    ))}
  </div>
);

export const ThankYouReferral = () => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal className="relative overflow-hidden rounded-[2rem] bg-[#0b130e] text-left shadow-[0_30px_70px_rgba(11,19,14,0.35)] mb-8" data-testid="thankyou-referral">
      <div className="absolute -top-24 -right-24 w-80 h-80 bg-[#1a5c3b] rounded-full blur-3xl opacity-50 animate-blob" />
      <div className="absolute -bottom-24 -left-16 w-72 h-72 bg-emerald-500 rounded-full blur-3xl opacity-20 animate-blob [animation-delay:3s]" />
      <Confetti />
      <div className="relative grid md:grid-cols-5 gap-6 md:gap-8 items-center p-5 md:p-10 lg:p-12">
        <div className="md:col-span-3 min-w-0">
          <span className="inline-flex items-center gap-2 bg-white text-[#0b130e] text-[10px] md:text-[11px] font-bold uppercase tracking-[0.2em] md:tracking-[0.25em] px-3 md:px-4 py-1 md:py-1.5 mb-3 md:mb-5 rounded-full">
            <Gift className="w-3.5 h-3.5" />Programme de référence
          </span>
          <p className="md:hidden text-3xl font-extrabold text-[#4ade80] leading-none mb-2" data-testid="thankyou-referral-amount-mobile"><CountUp to={350} />&nbsp;$ <span className="text-sm font-semibold text-gray-300">comptant par ami</span></p>
          <Words parts={[['Vous connaissez quelqu’un qui cherche un véhicule? ']]} className="text-lg md:text-3xl font-bold !text-white leading-tight mb-1 md:mb-3" />
          <p className="text-xs md:text-base text-gray-300 mb-4 md:mb-6">Référez un ami. S’il devient notre client, <strong className="text-white">on vous remet 350 $ comptant</strong> pour vous remercier.</p>
          <div className="hidden md:flex flex-wrap gap-2 mb-7">
            {STEPS.map(([Icon, t], i) => (
              <span key={t} className="inline-flex items-center gap-2 text-xs md:text-sm text-white bg-white/10 border border-white/15 rounded-full px-3 md:px-3.5 py-1 md:py-1.5">
                <span className="w-5 h-5 rounded-full bg-[#4ade80] text-[#0b130e] text-xs font-bold flex items-center justify-center">{i + 1}</span>
                <Icon className="w-4 h-4 text-[#4ade80]" />{t}
              </span>
            ))}
          </div>
          <div className="flex flex-row gap-2 md:gap-3">
            <button type="button" onClick={() => setOpen(true)} className="group btn-shimmer inline-flex items-center justify-center gap-2 bg-[#4ade80] hover:bg-[#6ee79a] text-[#0b130e] font-bold whitespace-nowrap flex-1 md:flex-none text-sm md:text-base px-4 md:px-7 py-2.5 md:py-3.5 rounded-full hover:scale-[1.03] transition-[transform,background-color]" data-testid="thankyou-refer-btn">
              Référer un ami<ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <a href={`sms:?&body=${SMS_BODY}`} className="inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white font-semibold whitespace-nowrap flex-1 md:flex-none text-sm md:text-base px-4 md:px-6 py-2.5 md:py-3.5 rounded-full hover:bg-white/10 transition-colors" data-testid="thankyou-share-sms">
              <MessageSquare className="w-4 h-4" /><span className="md:hidden">Texto</span><span className="hidden md:inline">Partager par texto</span>
            </a>
          </div>
        </div>
        <div className="hidden md:flex md:col-span-2 justify-center">
          <div className="relative floaty">
            <div className="absolute inset-0 rounded-3xl bg-[#4ade80] blur-2xl opacity-30" />
            <div className="relative w-48 h-28 md:w-56 md:h-36 rounded-3xl bg-gradient-to-br from-[#4ade80] via-emerald-500 to-[#1a5c3b] rotate-[-6deg] shadow-2xl p-4 md:p-5 flex flex-col justify-between border border-white/30" data-testid="thankyou-referral-card">
              <div className="flex justify-between items-start">
                <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0b130e]/70">Credit Auto Reset</span>
                <Gift className="w-6 h-6 text-[#0b130e]/80" />
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-extrabold text-[#0b130e] leading-none tabular-nums"><CountUp to={350} />&nbsp;$</p>
                <p className="text-xs font-semibold text-[#0b130e]/70 mt-1">comptant, par ami référé</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ReferralModal open={open} onClose={() => setOpen(false)} language="fr" />
    </Reveal>
  );
};
